import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Drawer } from "@/components/Drawer";
import { ProviderGlyph } from "@/components/ProviderGlyph";
import { Skeleton } from "@/components/Skeleton";
import { Sparkline } from "@/components/Sparkline";
import { useToast } from "@/components/Toast";
import type { ProxyKey, ProxyKeyUsage, RealKey } from "@/types";

type SparkPoint = { day: string; requests: number; errors: number };

/** Tree view: each real key is a parent row; click the chevron (or the row
 *  anywhere outside the label link) to expand a list of its bound proxy keys.
 *  The existing /real-keys and /proxy-keys flat pages still work.
 */
export function Keys() {
  const [reals, setReals] = useState<RealKey[] | null>(null);
  const [proxies, setProxies] = useState<ProxyKey[] | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [drawerProxyId, setDrawerProxyId] = useState<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    Promise.all([
      api<RealKey[]>("/api/admin/real-keys"),
      api<ProxyKey[]>("/api/admin/proxy-keys"),
    ])
      .then(([r, p]) => {
        setReals(r);
        setProxies(p);
      })
      .catch((e) => setErr(e?.message ?? "load failed"));
  }, []);

  const proxiesByReal = useMemo(() => {
    const m = new Map<number, ProxyKey[]>();
    (proxies ?? []).forEach((p) => {
      const list = m.get(p.real_key_id) ?? [];
      list.push(p);
      m.set(p.real_key_id, list);
    });
    return m;
  }, [proxies]);

  const filteredReals = useMemo(() => {
    if (!reals) return null;
    if (!query.trim()) return reals;
    const q = query.toLowerCase();
    return reals.filter((r) => {
      if (
        r.label.toLowerCase().includes(q) ||
        r.provider.toLowerCase().includes(q) ||
        r.upstream_base_url.toLowerCase().includes(q)
      )
        return true;
      const children = proxiesByReal.get(r.id) ?? [];
      return children.some(
        (p) =>
          p.label.toLowerCase().includes(q) ||
          (p.project ?? "").toLowerCase().includes(q) ||
          p.prefix.toLowerCase().includes(q)
      );
    });
  }, [reals, query, proxiesByReal]);

  function toggle(id: number) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  }

  function expandAll() {
    if (!reals) return;
    setExpanded(new Set(reals.map((r) => r.id)));
  }

  function collapseAll() {
    setExpanded(new Set());
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl display">Keys</h1>
          <p className="text-sm text-slate-400">
            Real keys (upstream secrets) with their bound proxy keys nested underneath.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary text-xs" onClick={expandAll} disabled={!reals}>
            Expand all
          </button>
          <button
            className="btn-secondary text-xs"
            onClick={collapseAll}
            disabled={expanded.size === 0}
          >
            Collapse all
          </button>
        </div>
      </header>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <input
            type="search"
            className="input pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search keys (label, provider, project, prefix…)"
          />
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">⌕</span>
        </div>
        <div className="text-xs text-slate-500">
          {reals === null || proxies === null
            ? "…"
            : `${filteredReals?.length ?? 0} real · ${proxies.filter((p) => !p.revoked_at).length} active proxy`}
        </div>
      </div>

      {err && <div className="card text-rose-300 text-sm">{err}</div>}

      <div className="card p-0 overflow-hidden">
        {reals === null && (
          <div className="p-6 space-y-3">
            <Skeleton height={20} />
            <Skeleton height={20} />
            <Skeleton height={20} />
          </div>
        )}

        {filteredReals && filteredReals.length === 0 && (
          <div className="py-10 px-6 text-center text-slate-400">
            {query ? `No matches for "${query}".` : "No real keys yet."}
          </div>
        )}

        <ul className="divide-y divide-white/5">
          {filteredReals?.map((r) => {
            const children = proxiesByReal.get(r.id) ?? [];
            const active = children.filter((p) => !p.revoked_at);
            const isOpen = expanded.has(r.id);
            const days = Math.floor(
              (Date.now() - new Date(r.rotated_at).getTime()) / 86400000
            );
            const edge =
              days >= 180 ? "row-edge-err" : days >= 90 ? "row-edge-warn" : "row-edge-ok";
            return (
              <li key={r.id} className={edge}>
                <div
                  className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors cursor-pointer"
                  onClick={() => toggle(r.id)}
                >
                  <span
                    className="inline-block w-4 text-slate-500 transition-transform"
                    style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                    aria-hidden
                  >
                    ▸
                  </span>
                  <ProviderGlyph provider={r.provider} />
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/real-keys/${r.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium hover:text-accent-500"
                    >
                      {r.label}
                    </Link>
                    <div className="mono text-[11px] text-slate-500 truncate">
                      {r.provider} · {r.upstream_base_url}
                    </div>
                  </div>
                  <div className="flex gap-3 items-center text-xs text-slate-500">
                    <span>
                      {active.length} proxy{active.length === 1 ? "" : "s"}
                    </span>
                    <span className="badge-gold">{days}d</span>
                  </div>
                </div>
                {isOpen && (
                  <ul className="pl-14 pr-4 pb-3 space-y-1 border-t border-white/5 bg-white/[0.015]">
                    {children.length === 0 && (
                      <li className="text-xs text-slate-500 py-2">
                        No proxy keys bound yet.{" "}
                        <Link
                          to="/proxy-keys?new=1"
                          onClick={(e) => e.stopPropagation()}
                          className="text-accent-500 hover:underline"
                        >
                          Issue one →
                        </Link>
                      </li>
                    )}
                    {children.map((p) => {
                      const expired =
                        p.expires_at && new Date(p.expires_at) < new Date();
                      return (
                        <li
                          key={p.id}
                          className="flex items-center justify-between py-1.5 text-sm gap-2"
                        >
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDrawerProxyId(p.id);
                              }}
                              className="text-[10px] text-slate-500 hover:text-accent-500 px-1.5 py-0.5 rounded border border-white/5 hover:border-accent-500/30"
                              title="Quick look without leaving this page"
                            >
                              👁
                            </button>
                            <Link
                              to={`/proxy-keys/${p.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-accent-500 truncate"
                            >
                              <span className="mono text-xs text-slate-500">{p.prefix}…</span>{" "}
                              {p.label}{" "}
                              <span className="text-slate-500 text-xs">
                                · {p.project ?? "—"}
                              </span>
                            </Link>
                          </div>
                          {p.revoked_at ? (
                            <span className="badge-err text-[10px]">revoked</span>
                          ) : expired ? (
                            <span className="badge-err text-[10px]">expired</span>
                          ) : (
                            <span className="badge-ok text-[10px]">active</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <ProxyQuickLookDrawer
        proxyKeyId={drawerProxyId}
        onClose={() => setDrawerProxyId(null)}
        onRevoked={(id) => {
          toast.success("Proxy key revoked");
          setProxies((prev) =>
            prev?.map((p) =>
              p.id === id ? { ...p, revoked_at: new Date().toISOString() } : p
            ) ?? null
          );
        }}
      />
    </div>
  );
}

function ProxyQuickLookDrawer({
  proxyKeyId,
  onClose,
  onRevoked,
}: {
  proxyKeyId: number | null;
  onClose: () => void;
  onRevoked: (id: number) => void;
}) {
  const [row, setRow] = useState<ProxyKey | null>(null);
  const [usage, setUsage] = useState<ProxyKeyUsage | null>(null);
  const [series, setSeries] = useState<SparkPoint[] | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (proxyKeyId === null) {
      setRow(null);
      setUsage(null);
      setSeries(null);
      return;
    }
    Promise.all([
      api<ProxyKey>(`/api/admin/proxy-keys/${proxyKeyId}`),
      api<ProxyKeyUsage>(`/api/admin/proxy-keys/${proxyKeyId}/usage`),
      api<{ days: number; series: SparkPoint[] }>(
        `/api/admin/proxy-keys/${proxyKeyId}/timeseries?days=7`
      ),
    ])
      .then(([r, u, t]) => {
        setRow(r);
        setUsage(u);
        setSeries(t.series);
      })
      .catch((e) => toast.error(e?.message ?? "load failed"));
  }, [proxyKeyId, toast]);

  async function revoke() {
    if (!row) return;
    if (!window.confirm("Revoke this proxy key? Consumers will get 401 immediately.")) return;
    setBusy(true);
    try {
      await api(`/api/admin/proxy-keys/${row.id}/revoke`, { method: "POST" });
      onRevoked(row.id);
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "revoke failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open={proxyKeyId !== null}
      onClose={onClose}
      title={row ? row.label : "Loading…"}
      viewFullHref={row ? `/proxy-keys/${row.id}` : undefined}
      width={520}
    >
      {!row ? (
        <div className="space-y-3">
          <Skeleton height={16} />
          <Skeleton height={16} />
          <Skeleton height={16} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <QuickStat label="Requests" value={usage?.total_requests ?? 0} />
            <QuickStat label="Errors" value={usage?.error_count ?? 0} />
            <QuickStat label="Avg ms" value={Math.round(usage?.avg_latency_ms ?? 0)} />
          </div>
          {series && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Last 7 days</div>
              <Sparkline data={series} width={480} height={56} />
            </div>
          )}
          <div className="text-xs text-slate-400 space-y-1">
            <div>
              <span className="text-slate-500">Prefix:</span>{" "}
              <span className="mono text-slate-300">{row.prefix}…</span>
            </div>
            <div>
              <span className="text-slate-500">Project:</span> {row.project ?? "—"}
            </div>
            <div>
              <span className="text-slate-500">Expires:</span>{" "}
              {row.expires_at ? new Date(row.expires_at).toLocaleString() : "never"}
            </div>
            <div>
              <span className="text-slate-500">Allowed paths:</span>{" "}
              {row.allowed_path_prefixes?.length
                ? row.allowed_path_prefixes.join(", ")
                : "any"}
            </div>
            <div>
              <span className="text-slate-500">IP allowlist:</span>{" "}
              {row.ip_allowlist?.length ? row.ip_allowlist.join(", ") : "any"}
            </div>
            {row.revoked_at && (
              <div className="text-rose-300">
                Revoked {new Date(row.revoked_at).toLocaleString()}
              </div>
            )}
          </div>
          {!row.revoked_at && (
            <div className="flex justify-end">
              <button className="btn-danger" onClick={revoke} disabled={busy}>
                {busy ? "Revoking…" : "Revoke"}
              </button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function QuickStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-100">{value}</div>
    </div>
  );
}
